import * as React from 'react';

import { headingVariants, type HeadingVariantProps } from './heading.variants';
import { cn } from '@/hen-ui/utils';
import { Slot } from '@radix-ui/react-slot';

export interface HeadingProps
  extends Omit<
      React.PropsWithChildren<React.HTMLAttributes<HTMLHeadingElement>>,
      'color'
    >,
    HeadingVariantProps {
  className?: string;
  asChild?: boolean;
  variant: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

/**
 * Heading component following HEN Design System standards.
 *
 * @example
 * // Basic usage
 * <Heading variant="h1">Heading H1</Heading>
 *
 * @example
 * // With HEN variant and size
 * <Heading variant="h1">
 *   Heading H1
 * </Heading>
 *
 * @example
 * // As a link using asChild
 * <Heading asChild variant="h1">
 *   <a href="/about">Heading H1</a>
 * </Heading>
 */
export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  (
    {
      className,
      variant = 'h1',
      color,
      align,
      children,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : variant;

    return (
      <Comp
        className={cn(headingVariants({ variant, color, align }), className)}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);

Heading.displayName = 'Heading';
